import { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button, Input } from '@lms/ui';
import { apiClient, updateApiClientToken } from '../lib/api-client';
import { getNeonAuthClient, getNeonJwtToken } from '../lib/neon-auth';
import { ArrowRight, Mail, Lock, Phone, User } from 'lucide-react';
import { useLanguage } from '../lib/LanguageContext';
import { copy } from '../copy/copy';
import { Bilingual } from '../components/Bilingual';

const signUpSchema = z
  .object({
    name: z.string().min(2, 'nameMin'),
    email: z.string().email('emailInvalid'),
    phone: z.string().min(10, 'phoneMin'),
    gender: z.enum(['MALE', 'FEMALE']),
    password: z.string().min(8, 'passwordMin'),
    confirmPassword: z.string().min(8, 'confirmPasswordMin'),
  })
  .refine(data => data.password === data.confirmPassword, {
    message: 'passwordsDontMatch',
    path: ['confirmPassword'],
  });

type SignUpInput = z.infer<typeof signUpSchema>;

interface ParticipantDestination {
  pathname: string;
  search?: string;
  hash?: string;
}

interface SignUpNavigationState {
  from?: ParticipantDestination;
}

export function SignUp() {
  const navigate = useNavigate();
  const location = useLocation();
  const navigationState = location.state as SignUpNavigationState | null;
  const legacyProgramCode = new URLSearchParams(location.search).get('program');
  const destination =
    navigationState?.from ??
    (legacyProgramCode
      ? { pathname: `/programs/${encodeURIComponent(legacyProgramCode)}/register` }
      : undefined);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const { language, setLanguage, t } = useLanguage();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm<SignUpInput>({
    resolver: zodResolver(signUpSchema),
  });

  const gender = useWatch({
    control,
    name: 'gender',
  });

  const getErrorMsg = (key?: string) => {
    if (!key) return undefined;
    const errorCopy = copy.signup.errors[key as keyof typeof copy.signup.errors];
    return errorCopy ? t(errorCopy) : key;
  };

  const onSubmit = async (data: SignUpInput) => {
    setMessage(null);
    try {
      const response = await getNeonAuthClient().signUp.email({
        email: data.email,
        password: data.password,
        name: data.name,
        callbackURL: `${window.location.origin}/verify-email`,
      });
      if (response.error) throw new Error(response.error.message);

      const token = await getNeonJwtToken();
      if (token) {
        updateApiClientToken(token);
        await apiClient.auth.syncProfile({
          name: data.name,
          phone: data.phone,
          gender: data.gender,
        });
      }

      setMessage({
        type: 'success',
        text: t(copy.signup.successMsg),
      });

      void navigate('/login', {
        state: {
          message: {
            type: 'success',
            text: 'Pendaftaran berhasil. Periksa email Anda untuk verifikasi, lalu masuk menggunakan email dan kata sandi yang baru dibuat.',
          },
          from: destination,
        },
      });
    } catch (e: unknown) {
      const error = e as Error;
      setMessage({ type: 'error', text: error.message || t(copy.signup.errorMsg) });
    }
  };

  const features = [
    copy.signup.features.curriculum,
    copy.signup.features.track,
    copy.signup.features.community,
    copy.signup.features.progress,
  ];

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-surface font-body text-on-surface overflow-x-hidden">
      {/* Heritage Branding Plane (Left Side) */}
      <div className="hidden md:flex md:w-1/2 lg:w-[40%] bg-primary text-on-primary relative flex-col justify-center p-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary to-primary-container opacity-95" />
        <div
          className="absolute inset-0 opacity-[0.05] mix-blend-overlay"
          style={{
            backgroundImage:
              "url('https://www.transparenttextures.com/patterns/natural-paper.png')",
          }}
        />

        <div className="relative z-10">
          <img
            alt="Tarbiyah Heritage Logo"
            className="h-16 w-auto mb-12 brightness-0 invert"
            src="/logo.png"
          />
          <h1 className="font-headline font-extrabold text-5xl tracking-tighter leading-tight mb-6">
            <Bilingual {...copy.signup.heroTitleStart} /> <br />
            <span className="text-secondary">
              <Bilingual {...copy.signup.heroTitleEnd} />
            </span>
          </h1>
          <p className="text-on-primary-container text-lg font-light leading-relaxed max-w-md opacity-90">
            <Bilingual {...copy.signup.heroSubtitle} />
          </p>

          <div className="mt-12 space-y-4">
            {features.map((feature, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-5 h-5 rounded-full bg-secondary/20 flex items-center justify-center">
                  <div className="w-2 h-2 rounded-full bg-secondary" />
                </div>
                <span className="text-sm font-medium opacity-80">
                  <Bilingual {...feature} />
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="absolute -bottom-48 -left-48 w-96 h-96 bg-secondary rounded-full opacity-10 blur-[150px]" />
      </div>

      {/* Interface Plane (Right Side) */}
      <div className="flex-1 flex items-center justify-center p-8 md:p-16 bg-surface-container-lowest">
        <div className="w-full max-w-md animate-in fade-in slide-in-from-bottom-4 duration-700">
          {/* Language Switcher */}
          <div className="flex justify-end mb-8">
            <div className="flex items-center bg-surface-container rounded-lg p-1 border border-on-surface/5">
              <button
                type="button"
                onClick={() => setLanguage('id')}
                className={`px-3 py-1 text-[11px] font-bold rounded transition-all ${
                  language === 'id'
                    ? 'bg-primary text-white shadow-sm'
                    : 'text-on-surface-variant hover:bg-surface-container-high'
                }`}
              >
                ID
              </button>
              <button
                type="button"
                onClick={() => setLanguage('ar')}
                className={`px-3 py-1 text-[11px] font-bold rounded transition-all ${
                  language === 'ar'
                    ? 'bg-primary text-white shadow-sm'
                    : 'text-on-surface-variant hover:bg-surface-container-high'
                }`}
              >
                AR
              </button>
            </div>
          </div>

          <div className="mb-10">
            <h2 className="font-headline font-bold text-3xl text-primary tracking-tight mb-2">
              {t(copy.signup.title)}
            </h2>
            <p className="text-on-surface-variant font-body text-base">{t(copy.signup.subtitle)}</p>
          </div>

          {message && (
            <div
              className={`mb-8 p-4 rounded-xl text-sm font-medium shadow-level1 ${
                message.type === 'success'
                  ? 'bg-success-container/30 text-on-success-container'
                  : 'bg-error-container/30 text-on-error-container'
              }`}
            >
              {message.text}
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            <Input
              label={t(copy.signup.nameLabel)}
              placeholder={t(copy.signup.namePlaceholder)}
              {...register('name')}
              error={getErrorMsg(errors.name?.message)}
              leftIcon={<User size={18} className="text-on-surface-variant/40" />}
            />

            <Input
              label={t(copy.signup.emailLabel)}
              type="email"
              placeholder={t(copy.signup.emailPlaceholder)}
              {...register('email')}
              error={getErrorMsg(errors.email?.message)}
              leftIcon={<Mail size={18} className="text-on-surface-variant/40" />}
            />

            <Input
              label={t(copy.signup.phoneLabel)}
              placeholder={t(copy.signup.phonePlaceholder)}
              {...register('phone')}
              error={getErrorMsg(errors.phone?.message)}
              leftIcon={<Phone size={18} className="text-on-surface-variant/40" />}
            />

            <div className="space-y-3">
              <label className="text-sm font-bold text-primary block ml-1">
                {t(copy.signup.genderLabel)}
              </label>
              <div className="grid grid-cols-2 gap-4">
                <label
                  className={`
                  flex items-center justify-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all
                  ${
                    gender === 'MALE'
                      ? 'border-primary bg-primary/5 text-primary shadow-level1'
                      : 'border-on-surface/5 bg-surface-container-lowest text-on-surface-variant hover:border-primary/20'
                  }
                `}
                >
                  <input type="radio" {...register('gender')} value="MALE" className="hidden" />
                  <span className="font-bold text-sm">{t(copy.signup.genderMale)}</span>
                </label>
                <label
                  className={`
                  flex items-center justify-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all
                  ${
                    gender === 'FEMALE'
                      ? 'border-tertiary bg-tertiary/5 text-tertiary shadow-level1'
                      : 'border-on-surface/5 bg-surface-container-lowest text-on-surface-variant hover:border-tertiary/20'
                  }
                `}
                >
                  <input type="radio" {...register('gender')} value="FEMALE" className="hidden" />
                  <span className="font-bold text-sm">{t(copy.signup.genderFemale)}</span>
                </label>
              </div>
              {errors.gender && (
                <p className="text-xs text-error mt-1 ml-1 font-medium">
                  {getErrorMsg(errors.gender.message)}
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label={t(copy.signup.passwordLabel)}
                type="password"
                placeholder={t(copy.signup.passwordPlaceholder)}
                {...register('password')}
                error={getErrorMsg(errors.password?.message)}
                leftIcon={<Lock size={18} className="text-on-surface-variant/40" />}
              />
              <Input
                label={t(copy.signup.confirmPasswordLabel)}
                type="password"
                placeholder={t(copy.signup.passwordPlaceholder)}
                {...register('confirmPassword')}
                error={getErrorMsg(errors.confirmPassword?.message)}
                leftIcon={<Lock size={18} className="text-on-surface-variant/40" />}
              />
            </div>

            <div className="pt-4">
              <Button
                type="submit"
                variant="primary"
                size="lg"
                isLoading={isSubmitting}
                className="w-full rounded-xl flex items-center justify-center gap-3 group bg-primary text-on-primary hover:bg-primary-container hover:text-on-primary-container transition-all"
              >
                {t(copy.signup.signupBtn)}
                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </Button>
            </div>
          </form>

          <div className="mt-8 text-center border-t border-outline-variant/10 pt-6">
            <p className="text-sm text-on-surface-variant font-body">
              {t(copy.signup.alreadyRegistered)}{' '}
              <Link
                to="/login"
                state={{ from: destination }}
                className="text-primary font-bold hover:underline"
              >
                {t(copy.signup.loginBtn)}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
